import { Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, TextField, Typography } from "@material-ui/core";
import { Field } from "react-final-form";
import { useRecordContext, CheckboxGroupInput, required, ChipField } from "react-admin";
import { enableMpmPlatformOrdering } from "../../constant";

export const platformChoices = [
  { id: "ios", name: "iOS" },
  { id: "android", name: "Android" },
  { id: "web", name: "Web" },
  { id: "stb", name: "STB" },
  { id: "android_tv", name: "Android TV" },
  { id: "samsung_tv", name: "Samsung TV" },
  { id: "lg_tv", name: "LG TV" },
];

export const platformDefaultValue = ["ios", "android", "web", "stb", "android_tv", "samsung_tv", "lg_tv"];

export const platformWithOrderDefaultValue = platformDefaultValue.map((platform) => ({
  platform,
  order: "",
}));

//NOTE: Add useDefaultValue props because
//In mpm-rows (Refer to mpm-rows-form-content.js), don't know why Input with defaultValues crash the page, so, set the value when the page is loaded instead.
export const PlatformCheckboxGroupInput = ({ disabled, ...props }) => {
  var defaultValue;
  if (props.useDefaultValue) {
    defaultValue = platformDefaultValue;
  }

  return (
    <CheckboxGroupInput //
      label="Platform"
      source="platform"
      optionText="name"
      optionValue="id"
      choices={platformChoices}
      defaultValue={defaultValue}
      validate={[required()]}
      disabled={disabled ?? false}
    />
  );
};

const validatePlatformWithOrder = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return "At least one platform must be selected";
  }

  for (const item of value) {
    if (item.order === undefined || item.order === null || String(item.order).trim() === "") {
      continue;
    }

    if (!/^\d+$/.test(String(item.order).trim())) {
      const choice = platformChoices.find((platform) => platform.id === item.platform);
      return `${choice ? choice.name : item.platform} row order must be a whole number or empty`;
    }
  }

  return undefined;
};

export const PlatformWithOrderCheckboxGroupInput = ({ disabled, source = "platform_with_order" }) => {
  if (!enableMpmPlatformOrdering) {
    return <PlatformCheckboxGroupInput disabled={disabled} />;
  }

  const getPlatformItem = (value, platform) => {
    if (!Array.isArray(value)) {
      return null;
    }

    return value.find((item) => item.platform === platform.id);
  };

  const updatePlatform = (value, platform, checked) => {
    const currentValue = Array.isArray(value) ? value : [];

    if (!checked) {
      return currentValue.filter((item) => item.platform !== platform.id);
    }

    if (getPlatformItem(currentValue, platform)) {
      return currentValue;
    }

    return [...currentValue, { platform: platform.id, order: "" }];
  };

  const updateOrder = (value, platform, order) => {
    const currentValue = Array.isArray(value) ? value : [];

    return currentValue.map((item) => {
      if (item.platform !== platform.id) {
        return item;
      }

      return {
        ...item,
        order,
      };
    });
  };

  return (
    <Field name={source} validate={validatePlatformWithOrder}>
      {({ input, meta }) => (
        <FormControl component="fieldset" error={meta.touched && !!meta.error} disabled={disabled ?? false} fullWidth>
          {/* <Typography variant="caption" color="textSecondary">
            Platform *
          </Typography> */}
          <Grid container spacing={1} alignItems="center">
            <Grid item style={{ flex: "0 0 auto", minWidth: "140px" }}>
              <Typography variant="caption" color="textSecondary">
                Platform *
              </Typography>
            </Grid>
            <Grid item style={{ flex: "0 0 auto", width: "120px" }}>
              <Typography variant="caption" color="textSecondary">
                Row order
              </Typography>
            </Grid>
          </Grid>
          {platformChoices.map((platform) => {
            const item = getPlatformItem(input.value, platform);
            const checked = !!item;

            return (
              <Grid key={platform.id} container spacing={1} alignItems="center">
                <Grid item style={{ flex: "0 0 auto", minWidth: "140px" }}>
                  <FormControlLabel
                    style={{ marginRight: 0, whiteSpace: "nowrap" }}
                    control={
                      <Checkbox
                        color="primary"
                        checked={checked}
                        disabled={disabled ?? false}
                        onChange={(event) => {
                          input.onChange(updatePlatform(input.value, platform, event.target.checked));
                        }}
                      />
                    }
                    label={platform.name}
                  />
                </Grid>
                <Grid item style={{ flex: "0 0 auto", width: "120px" }}>
                  <TextField
                    value={checked ? (item.order ?? "") : ""}
                    disabled={!checked || (disabled ?? false)}
                    onChange={(event) => {
                      input.onChange(updateOrder(input.value, platform, event.target.value));
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: { textAlign: "center" },
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            );
          })}
          {meta.touched && meta.error ? <FormHelperText>{meta.error}</FormHelperText> : null}
        </FormControl>
      )}
    </Field>
  );
};

export const PlatformChipField = () => {
  const record = useRecordContext();

  return (
    <>
      {record.platform.map((item) => {
        const choice = platformChoices.find((i) => i.id == item);
        return <ChipField key={choice.id} record={{ key: choice.id, name: choice.name }} source="name" />;
      })}
    </>
  );
};
